import React, { useState, useRef, useCallback } from 'react';
import { Send, StopCircle, Paperclip, Mic, MicOff, Image, Code, Copy, X } from 'lucide-react';
import { Button, Input, Tooltip, TooltipContent, TooltipTrigger } from '../../mawi-ui';
import { cn } from '../../utils';

export interface AIChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  onVoiceToggle: () => void;
  onFileAttach: () => void;
  onImageUpload: () => void;
  isLoading: boolean;
  isRecording: boolean;
  isStreaming: boolean;
  micPermissionStatus: 'checking' | 'granted' | 'blocked' | 'prompt';
  attachedFiles: File[];
  onRemoveFile: (index: number) => void;
  disabled?: boolean;
  placeholder?: string;
  chatMode?: string;
  onChatModeChange?: (mode: string) => void;
  selectedModel?: string;
  onModelChange?: (modelId: string) => void;
  availableModels?: { id: string; name: string }[];
  modelStatus?: { text: string; color: string; bg: string } | null;
}

const chatModes = [
  { id: 'chat', name: 'Chat' },
  { id: 'code', name: 'Code' },
  { id: 'debug', name: 'Debug' },
  { id: 'explain', name: 'Explain' },
  { id: 'plan', name: 'Plan' },
  { id: 'report', name: 'Report' },
];

export const AIChatInput: React.FC<AIChatInputProps> = ({
  input,
  onInputChange,
  onSend,
  onStop,
  onVoiceToggle,
  onFileAttach,
  onImageUpload,
  isLoading,
  isRecording,
  isStreaming,
  micPermissionStatus,
  attachedFiles,
  onRemoveFile,
  disabled = false,
  placeholder = "Message...",
  chatMode = 'chat',
  onChatModeChange,
  selectedModel = '',
  onModelChange,
  availableModels = [],
  modelStatus = null,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && input.trim()) {
        onSend();
      }
    }
  };

  const handleCodeSnippet = useCallback(() => {
    const codeTemplate = `\`\`\`javascript
// Your code here
function example() {
    console.log("Hello, World!");
}
\`\`\``;
    onInputChange(input + (input ? '\n\n' : '') + codeTemplate);
  }, [input, onInputChange]);

  const copyInputToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(input);
    } catch (error) {}
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-0 bg-white dark:bg-gray-900 shadow-sm">
      {/* Attachments preview */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {attachedFiles.map((file, index) => (
            <div key={index} className="relative group">
              {file.type.startsWith('image/') ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-8 h-8 object-cover rounded border"
                  />
                  <button
                    onClick={() => onRemoveFile(index)}
                    className="absolute -top-1 -right-1 w-3 h-3 bg-destructive text-white rounded-full flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 p-1 bg-background rounded border text-[10px]">
                  <Paperclip className="h-2.5 w-2.5" />
                  <span className="truncate max-w-16">{file.name}</span>
                  <button
                    onClick={() => onRemoveFile(index)}
                    className="text-muted-foreground hover:text-foreground text-[10px]"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1.5 items-end">
        {/* Left side action buttons */}
        <div className="flex flex-col gap-1 flex-shrink-0 px-0.5">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            onChange={(e) => {
              if (e.target.files) {
                Array.from(e.target.files).forEach(f => attachedFiles.push(f));
              }
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 p-0"
            onClick={onFileAttach}
            disabled={isLoading || disabled}
            title="Attach files"
          >
            <Paperclip className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 p-0"
            onClick={onImageUpload}
            disabled={isLoading || disabled}
            title="Upload images"
          >
            <Image className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 p-0"
            onClick={handleCodeSnippet}
            disabled={isLoading || disabled}
            title="Insert code snippet"
          >
            <Code className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Textarea */}
        <div className="relative flex-1 chat-input">
          <textarea
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full min-h-[60px] max-h-[120px] resize-y px-2 py-1.5 pr-10 bg-transparent focus:outline-none text-xs border-0"
            disabled={isLoading || disabled}
            rows={5}
          />
          {input.trim() && !isLoading && (
            <div className="absolute top-1 right-1 flex gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                onClick={copyInputToClipboard}
                title="Copy message"
              >
                <Copy className="h-2.5 w-2.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                onClick={() => onInputChange('')}
                title="Clear message"
              >
                <X className="h-2.5 w-2.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Right side buttons - Mic and Send */}
        <div className="flex flex-col gap-1 flex-shrink-0 px-0.5">
          {/* Microphone button */}
          <Button
            variant={
              micPermissionStatus === 'blocked' ? "secondary" :
              isRecording ? "destructive" : "ghost"
            }
            size="icon"
            className="h-7 w-7 p-0"
            onClick={onVoiceToggle}
            disabled={micPermissionStatus === 'checking' || disabled}
          >
            {micPermissionStatus === 'blocked' ? (
              <MicOff className="h-3.5 w-3.5 opacity-50" />
            ) : isRecording ? (
              <MicOff className="h-3.5 w-3.5" />
            ) : (
              <Mic className="h-3.5 w-3.5" />
            )}
          </Button>

          {/* Send/Stop button */}
          {isLoading || isStreaming ? (
            <Button
              variant="default"
              size="icon"
              className="h-7 w-7 p-0"
              onClick={onStop}
              disabled={disabled}
            >
              <StopCircle className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="default"
              size="icon"
              className="h-7 w-7 p-0"
              onClick={onSend}
              disabled={!input.trim() || disabled}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Mode & Model Selectors */}
      <div className="flex gap-2 mt-2">
        <select
          value={chatMode}
          onChange={(e) => onChatModeChange?.(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          disabled={disabled || isLoading}
        >
          {chatModes.map(mode => (
            <option key={mode.id} value={mode.id}>{mode.name}</option>
          ))}
        </select>

        <select
          value={selectedModel}
          onChange={(e) => onModelChange?.(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          disabled={disabled || isLoading}
        >
        {availableModels.map(model => {
          // Clean short model display name
          let shortName = model.name
            .replace(/instruct$/i, '')
            .replace(/v\d+.*$/i, '')
            .replace(/[\-_\.].*$/, '')
            .trim();
          
          // Add version number if present
          const versionMatch = model.name.match(/[\-_\.](\d+\.\d+B)/i);
          if (versionMatch) {
            shortName += ` ${versionMatch[1]}`;
          }
          
          return (
            <option key={model.id} value={model.id}>
              {shortName.charAt(0).toUpperCase() + shortName.slice(1).toLowerCase()}
            </option>
          );
        })}
        </select>

        {modelStatus && (
          <span className={`text-xs px-1 font-semibold ${modelStatus.color}`}>
            {modelStatus.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default AIChatInput;